import { useEffect, useState, useCallback, useMemo } from 'react';
import GlobalDataRefreshManager from '../services/GlobalDataRefreshManager';

interface RefreshStatus {
  isRefreshing: boolean;
  lastRefresh: Date | null;
  nextRefresh: Date | null;
  intervalMinutes: number;
}

export const useGlobalDataRefresh = () => {
  const [status, setStatus] = useState<RefreshStatus>({
    isRefreshing: false,
    lastRefresh: null,
    nextRefresh: null,
    intervalMinutes: 30
  });

  // Use useMemo to ensure stable reference across renders
  const refreshManager = useMemo(() => GlobalDataRefreshManager.getInstance(), []);

  // Update status when refresh occurs
  const updateStatus = useCallback(() => {
    const newStatus = refreshManager.getStatus();
    setStatus(newStatus);
  }, [refreshManager]);

  useEffect(() => {
    // Initial status update
    updateStatus();

    // Subscribe to refresh events
    const unsubscribe = refreshManager.onRefresh(updateStatus);

    return () => {
      unsubscribe();
    };
  }, [refreshManager, updateStatus]);

  // Manual refresh trigger (for admin users or emergency refresh)
  const triggerManualRefresh = useCallback(async () => {
    try {
      await refreshManager.triggerManualRefresh();
    } catch (error) {
      console.error('Manual refresh failed:', error);
      throw error;
    }
  }, [refreshManager]);

  // Update refresh configuration
  const updateRefreshConfig = useCallback((config: {
    intervalMinutes?: number;
    skipWeekends?: boolean;
    businessHoursOnly?: boolean;
    businessHours?: { start: number; end: number };
    timezone?: string;
  }) => {
    refreshManager.updateConfig(config);
    updateStatus();
  }, [refreshManager, updateStatus]);

  // Admin-only reset
  const reset = useCallback(async () => {
    try {
      await refreshManager.reset();
    } catch (error) {
      console.error('Reset failed:', error);
      throw error;
    }
  }, [refreshManager]);

  return {
    status,
    triggerManualRefresh,
    updateRefreshConfig,
    reset,
    refreshManager
  };
};
